// The workspace and membership API.
//
// Every call carries credentials, because the session lives in a cookie the
// page cannot read. Every failure carries the SERVER'S message: these routes
// answer with sentences written for the person reading them — "a personal
// workspace has one member and cannot be shared", "that would leave the
// workspace with no administrator" — and a client that paraphrases them loses
// the only explanation of why an action was refused.
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';

export type Role = 'viewer' | 'member' | 'workspace_admin';

/** Ordered least to most, matching the server's own ordering. */
export const ROLES: Role[] = ['viewer', 'member', 'workspace_admin'];

/**
 * The names people see. ONE table, because the two shells had two: the
 * community screens called `member` "Editor" (the design's word) and the
 * enterprise ones called it "Member", so the same grant was two roles
 * depending on which page you were reading. "Editor" wins — it says what the
 * role does, which is the whole difference from Viewer.
 */
export const ROLE_LABEL: Record<Role, string> = {
  viewer: 'Viewer',
  member: 'Editor',
  workspace_admin: 'Administrator',
};

/**
 * What each role may do, in the order the roles gain it. Shown under the role
 * picker when inviting, so the sentence is the promise the product makes.
 *
 * KEEP THIS TRUE TO THE SERVER. The administrator line used to claim admins
 * were the ones who could "delete collections", which no route has ever
 * enforced: DELETE /collections/:id takes any non-viewer, and deleting a
 * collection destroys every slide in it. An editor reading that sentence
 * believed they could not do the single most destructive thing they could in
 * fact do.
 */
export const ROLE_HINT: Record<Role, string> = {
  viewer:
    'Viewers can open everything in this workspace, including private collections, but cannot upload or change anything.',
  member:
    'Editors can upload studies and create, edit and delete collections and groups — deleting a collection deletes the studies in it.',
  workspace_admin:
    'Administrators can also invite people, change roles, rename the workspace, and copy collections to another workspace they administer.',
};

export interface Workspace {
  public_id: string;
  name: string;
  role: Role;
  members: number;
  /** Where this account's uploads land. Exactly one workspace, or none. */
  is_default: boolean;
  /**
   * Being deleted (or archived before deleting replaced archiving): revoked for
   * everyone, listed so its deletion can be seen, or started again.
   *
   * An archived workspace grants nothing — the scope resolver drops it — so
   * this row is a name and a Delete button, not access.
   */
  archived: boolean;
  /**
   * The caller OWNS it: its storage counts against them, whoever uploads
   * (server catalog/storage.ts). Until they hand it to another administrator.
   */
  is_owner: boolean;
}

export interface Member {
  user_id: string;
  email: string | null;
  display_name: string;
  role: Role;
  created_utc: string;
  /** Owns the workspace: its storage counts against them. Always an administrator. */
  is_owner: boolean;
}

/**
 * Somebody invited who has not joined yet.
 *
 * Listed beside members rather than instead of them: a person you invited
 * yesterday is neither absent nor present, and a dialog showing only the joined
 * ones invites you to invite them again — which is how somebody ends up with
 * three links and no idea which one works.
 */
export interface Invitation {
  public_id: string;
  email: string;
  role: Role;
  created_utc: string;
  expires_utc: string;
}

export class ApiError extends HttpError {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${getConfig().apiRoot}${path}`, {
      credentials: 'include',
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new ApiError(body.error || `Request failed (${res.status})`, res.status);
  return body as T;
}

/**
 * Path segments are encoded, even though both ids are server-generated.
 *
 * A public id is `ws_` and hex and a user id is a uuid, so neither can carry a
 * slash today — but a URL assembled from data is a URL that can be assembled
 * wrongly, and the day an id format gains a character this would traverse
 * rather than 404. Free, and it removes the need to keep verifying the claim.
 */
const seg = encodeURIComponent;

export const listWorkspaces = (): Promise<{ workspaces: Workspace[] }> => call('/workspaces');

export const createWorkspace = (name: string): Promise<Workspace> =>
  call('/workspaces', { method: 'POST', body: JSON.stringify({ name }) });

export const renameWorkspace = (id: string, name: string): Promise<Workspace> =>
  call(`/workspaces/${seg(id)}`, { method: 'PATCH', body: JSON.stringify({ name }) });

/**
 * Delete a workspace and everything in it, for good. `confirm` repeats its name.
 * The server closes it at once and removes its contents in the background.
 */
export const deleteWorkspace = (id: string, confirm: string): Promise<{ deleting: boolean }> =>
  call(`/workspaces/${seg(id)}`, { method: 'DELETE', body: JSON.stringify({ confirm }) });

export const listMembers = (id: string): Promise<{ members: Member[]; invitations: Invitation[] }> =>
  call(`/workspaces/${seg(id)}/members`);

/**
 * Hand the workspace to another administrator: its storage counts against
 * them from now on. Only its owner can; refused when it does not fit in the
 * other person's free storage.
 */
export const transferOwnership = (id: string, userId: string): Promise<{ owner: string }> =>
  call(`/workspaces/${seg(id)}/owner`, { method: 'POST', body: JSON.stringify({ userId }) });

/**
 * Add by address — or INVITE, when nobody here has that address.
 *
 * The server decides which, and says so: 201 with a member means they were
 * already here, 202 with `invited` means a link is in their inbox. It used to
 * answer 404 "they need to sign up on this deployment first", which is an
 * instruction neither party can act on.
 */
export const addMember = (
  id: string,
  email: string,
  role: Role,
): Promise<Member | { invited: true; email: string; role: Role }> =>
  call(`/workspaces/${seg(id)}/members`, { method: 'POST', body: JSON.stringify({ email, role }) });

/** Withdraw an invitation nobody has accepted. */
export const revokeInvitation = (id: string, inviteId: string): Promise<void> =>
  call(`/workspaces/${seg(id)}/invitations/${seg(inviteId)}`, { method: 'DELETE' });

export const setMemberRole = (id: string, userId: string, role: Role): Promise<Member> =>
  call(`/workspaces/${seg(id)}/members/${seg(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });

/**
 * Point this account's uploads at a workspace.
 *
 * PUT rather than PATCH because it is idempotent and total: there is one
 * pointer and this sets it, so repeating the call says the same thing.
 */
export const setDefaultWorkspace = (id: string): Promise<void> =>
  call(`/workspaces/${seg(id)}/default`, { method: 'PUT' });

export const removeMember = (id: string, userId: string): Promise<void> =>
  call(`/workspaces/${seg(id)}/members/${seg(userId)}`, { method: 'DELETE' });
